import type {
  AffecterNouveauPosteByIdsDto,
  BaseContactMetier,
  BatchCreateContactMetierDto,
  ContactMetier,
  ContactMetierQueryFilter,
  CreateContactMetierDto,
  PageableDto,
} from "models";
import { Endpoints } from "enums";
import { cometApiClient } from "./apiClients";
import type { Operation } from "fast-json-patch";

function isStringifiable(value: unknown): value is { toString: () => string } {
  return value != null && typeof value.toString === "function";
}

/**
 * Récupère tous les contacts métiers, par paquets de 1000.
 * Le continuationToken permet de récupérer les contacts métiers suivants.
 * @returns PageableDto<Array<ContactMetier>
 */
async function getAll(continuationToken?: string): Promise<PageableDto<Array<ContactMetier>>> {
  return getByFilter(undefined, continuationToken);
}

/**
 * Récupère les contacts métiers correspondant à plusieurs filtres, rangés par ordre alphabétique de métier
 * @returns PageableDto<Array<ContactMetierTerritoire>
 */
async function getByFilter<T extends BaseContactMetier = ContactMetier>(
  filter: ContactMetierQueryFilter = {},
  continuationToken?: string
): Promise<PageableDto<Array<T>>> {
  const params = new URLSearchParams();
  if (continuationToken != null && continuationToken !== "") {
    params.append("continuationToken", continuationToken);
  }

  for (const [key, value] of Object.entries(filter)) {
    if (Array.isArray(value) && value.length > 0) {
      params.append(key, value.join(","));
    } else if (isStringifiable(value)) {
      params.append(key, value.toString());
    }
  }

  const queryString = params.size > 0 ? `?${params.toString()}` : "";

  const url = `${Endpoints.CONTACTS_METIERS}${queryString}`;
  const { values, ...reste } = await cometApiClient.get<
    PageableDto<Array<T>>,
    PageableDto<Array<T>>
  >(url);
  values.sort((cm1, cm2) => cm1.metier.libelle.localeCompare(cm2.metier.libelle));
  return { ...reste, values };
}

/**
 * Récupère un contact métier par son id
 * @returns Promise<ContactMetier>
 */
async function getById(id: string): Promise<ContactMetier> {
  const url = `${Endpoints.CONTACTS_METIERS}/${id}`;
  return cometApiClient.get<ContactMetier, ContactMetier>(url);
}

/**
 * Modifie un Contact Métier
 * @returns Promise<void>
 */
async function patch(id: string, data: Array<Operation>): Promise<void> {
  const url = `${Endpoints.CONTACTS_METIERS}/${id}`;
  return cometApiClient.patch(url, data);
}

/**
 * Modifie les postes de plusieurs Contacts Métiers
 * @returns Promise<void>
 */
async function multipleModifyPoste(payload: AffecterNouveauPosteByIdsDto): Promise<void> {
  const url = `${Endpoints.CONTACTS_METIERS}/${Endpoints.AFFECTER_POSTES}`;
  return cometApiClient.post(url, payload);
}

/**
 * Supprime un contact métier par son id
 * @returns Promise<void>
 */
async function deleteById(id: string): Promise<void> {
  const url = `${Endpoints.CONTACTS_METIERS}/${id}`;
  return cometApiClient.delete(url);
}

/**
 * "Nettoie" un DTO de création de Contact Métier afin de s'assurer que `$type` est bien
 * le **premier attribut de l'objet** avant de l'envoyer, sans quoi le back pète une 500
 * @param contactMetier `CreateContactMetierDto` le dto à "nettoyer"
 * @returns le dto "nettoyé"
 */
function cleanContactMetier(contactMetier: CreateContactMetierDto): CreateContactMetierDto {
  const { $type, ...contact } = contactMetier;
  return { $type, ...contact };
}

/**
 * Crée un contact métier
 * @returns `ContactMetier` le ContactMetier créé
 */
async function create(data: CreateContactMetierDto): Promise<ContactMetier> {
  const url = Endpoints.CONTACTS_METIERS;
  return cometApiClient.post<ContactMetier, ContactMetier>(url, cleanContactMetier(data));
}

/**
 * Crée plusieurs contacts métiers
 * @param data les contacts métiers à créer
 * @returns `Promise<void>`
 */
async function batchCreate(data: BatchCreateContactMetierDto): Promise<void> {
  const url = `${Endpoints.CONTACTS_METIERS}/${Endpoints.CREATION_EN_MASSE}`;
  const _data: BatchCreateContactMetierDto = { contacts: data.contacts.map(cleanContactMetier) };
  return cometApiClient.post(url, _data);
}

export const contactsMetiersService = {
  getAll,
  getByFilter,
  getById,
  create,
  batchCreate,
  patch,
  deleteById,
  multipleModifyPoste,
};
